import { useState, useRef, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

type MotionEffect = 'zoom-in' | 'zoom-out' | 'pan-left' | 'pan-right' | 'pan-up';

const DURATION = 4;
const FPS = 30;
const TOTAL_FRAMES = DURATION * FPS;

export default function ImageToVideo() {
  const { t } = useTranslation();
  const [sourceImage, setSourceImage] = useState<string | null>(null);
  const [sourceFileName, setSourceFileName] = useState('');
  const [effect, setEffect] = useState<MotionEffect>('zoom-in');
  const [isGenerating, setIsGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    return () => {
      if (videoUrl) URL.revokeObjectURL(videoUrl);
    };
  }, [videoUrl]);

  const loadImage = (src: string): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = src;
    });

  const handleFileSelect = useCallback((file: File) => {
    if (!file.type.startsWith('image/')) {
      setError(t('i2v.invalidFile'));
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      setError(t('i2v.fileTooLarge'));
      return;
    }
    setError(null);
    setVideoUrl(null);
    setSourceFileName(file.name);
    const reader = new FileReader();
    reader.onload = (e) => setSourceImage(e.target?.result as string);
    reader.readAsDataURL(file);
  }, [t]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileSelect(file);
  }, [handleFileSelect]);

  const handlePaste = useCallback((e: React.ClipboardEvent) => {
    const items = e.clipboardData.items;
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.startsWith('image/')) {
        const file = items[i].getAsFile();
        if (file) handleFileSelect(file);
        break;
      }
    }
  }, [handleFileSelect]);

  const drawFrame = (
    ctx: CanvasRenderingContext2D,
    img: HTMLImageElement,
    frameIndex: number,
    canvasW: number,
    canvasH: number,
    motionEffect: MotionEffect
  ) => {
    const p = frameIndex / TOTAL_FRAMES;
    const imgAspect = img.width / img.height;
    const canvasAspect = canvasW / canvasH;
    let sx = 0, sy = 0, sw = img.width, sh = img.height;

    if (imgAspect > canvasAspect) {
      sw = img.height * canvasAspect;
      sx = (img.width - sw) / 2;
    } else {
      sh = img.width / canvasAspect;
      sy = (img.height - sh) / 2;
    }

    const maxZoom = 0.15;
    switch (motionEffect) {
      case 'zoom-in': {
        const scale = 1 + p * maxZoom;
        const cropW = sw / scale; const cropH = sh / scale;
        sx += (sw - cropW) / 2; sy += (sh - cropH) / 2;
        sw = cropW; sh = cropH; break;
      }
      case 'zoom-out': {
        const scale = 1 + (1 - p) * maxZoom;
        const cropW = sw / scale; const cropH = sh / scale;
        sx += (sw - cropW) / 2; sy += (sh - cropH) / 2;
        sw = cropW; sh = cropH; break;
      }
      case 'pan-left': {
        const panRange = sw * 0.12;
        sx += panRange * (1 - p); sw -= panRange; break;
      }
      case 'pan-right': {
        const panRange = sw * 0.12;
        sx += panRange * p; sw -= panRange; break;
      }
      case 'pan-up': {
        const panRange = sh * 0.12;
        sy += panRange * (1 - p); sh -= panRange; break;
      }
    }
    ctx.clearRect(0, 0, canvasW, canvasH);
    ctx.drawImage(img, sx, sy, sw, sh, 0, 0, canvasW, canvasH);
  };

  const handleGenerate = async () => {
    if (!sourceImage) return;

    setIsGenerating(true);
    setProgress(0);
    setVideoUrl(null);
    setError(null);

    try {
      const img = await loadImage(sourceImage);
      const canvas = canvasRef.current!;
      const canvasW = Math.min(img.width, 1280);
      const canvasH = Math.round(canvasW * (img.height / img.width));
      canvas.width = canvasW;
      canvas.height = canvasH;
      const ctx = canvas.getContext('2d')!;

      const mimeType = MediaRecorder.isTypeSupported('video/webm;codecs=vp9')
        ? 'video/webm;codecs=vp9'
        : MediaRecorder.isTypeSupported('video/webm;codecs=vp8')
        ? 'video/webm;codecs=vp8'
        : 'video/webm';

      const stream = canvas.captureStream(FPS);
      const recorder = new MediaRecorder(stream, { mimeType, videoBitsPerSecond: 5000000 });
      const chunks: Blob[] = [];
      recorder.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };
      const recordingDone = new Promise<void>((resolve) => { recorder.onstop = () => resolve(); });

      recorder.start();
      for (let i = 0; i <= TOTAL_FRAMES; i++) {
        drawFrame(ctx, img, i, canvasW, canvasH, effect);
        setProgress(Math.round((i / TOTAL_FRAMES) * 100));
        await new Promise((r) => setTimeout(r, 1000 / FPS));
      }
      recorder.stop();
      await recordingDone;

      const blob = new Blob(chunks, { type: mimeType });
      const url = URL.createObjectURL(blob);
      setVideoUrl(url);
    } catch (err) {
      console.error('Video generation failed:', err);
      setError(t('i2v.error'));
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDownload = () => {
    if (!videoUrl) return;
    const a = document.createElement('a');
    a.href = videoUrl;
    a.download = `ai-video-${Date.now()}.webm`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleReset = () => {
    if (videoUrl) URL.revokeObjectURL(videoUrl);
    setVideoUrl(null);
    setSourceImage(null);
    setSourceFileName('');
    setProgress(0);
    setError(null);
  };

  const effects: { id: MotionEffect; icon: string; labelKey: string }[] = [
    { id: 'zoom-in', icon: 'ri-zoom-in-line', labelKey: 'i2v.effectZoomIn' },
    { id: 'zoom-out', icon: 'ri-zoom-out-line', labelKey: 'i2v.effectZoomOut' },
    { id: 'pan-left', icon: 'ri-arrow-left-line', labelKey: 'i2v.effectPanLeft' },
    { id: 'pan-right', icon: 'ri-arrow-right-line', labelKey: 'i2v.effectPanRight' },
    { id: 'pan-up', icon: 'ri-arrow-up-line', labelKey: 'i2v.effectPanUp' },
  ];

  return (
    <div className="bg-[#252830] rounded-2xl p-8 shadow-lg border border-gray-800">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent/20">
          <i className="ri-video-line text-accent text-lg"></i>
        </div>
        <h3 className="font-black text-white text-base">{t('i2v.title')}</h3>
        <span className="px-2.5 py-1 bg-primary/20 rounded-full text-xs font-bold text-primary">{t('gen.free')}</span>
      </div>

      {/* 上传区域 */}
      {!sourceImage ? (
        <div
          onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
          onPaste={handlePaste}
          onClick={() => fileInputRef.current?.click()}
          className={`border-2 border-dashed rounded-xl p-10 text-center transition-all cursor-pointer ${
            isDragging ? 'border-primary bg-primary/5' : 'border-gray-700 hover:border-primary/50 hover:bg-[#1e2025]'
          }`}
        >
          <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 bg-accent/20 rounded-2xl">
            <i className="ri-image-add-line text-3xl text-accent"></i>
          </div>
          <p className="text-base text-gray-300 font-bold mb-1">{t('i2v.uploadTitle')}</p>
          <p className="text-sm text-gray-500 font-medium">{t('i2v.uploadHint')}</p>
          <p className="text-xs text-gray-600 mt-2 font-medium">{t('i2v.uploadFormats')}</p>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            capture={undefined}
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleFileSelect(file);
              e.target.value = '';
            }}
          />
        </div>
      ) : (
        <>
          {/* 预览 */}
          <div className="relative mb-6 rounded-xl overflow-hidden border border-gray-700">
            <div className="w-full h-48 bg-[#1e2025]">
              <img src={sourceImage} alt="Source" className="w-full h-full object-contain" />
            </div>
            <div className="absolute top-3 right-3">
              <button
                onClick={handleReset}
                disabled={isGenerating}
                className="px-3 py-1.5 bg-black/50 backdrop-blur-sm rounded-lg text-xs font-bold text-white hover:bg-black/70 transition-all cursor-pointer whitespace-nowrap disabled:opacity-50"
              >
                <i className="ri-close-line mr-1"></i>{t('i2v.changeImage')}
              </button>
            </div>
            <div className="absolute bottom-3 left-3 px-2.5 py-1 bg-black/50 backdrop-blur-sm rounded-lg text-xs text-white font-medium">
              {sourceFileName}
            </div>
          </div>

          {/* 运动效果选择 */}
          <div className="mb-6">
            <label className="block text-sm font-bold text-gray-300 mb-3">{t('i2v.effectLabel')}</label>
            <div className="grid grid-cols-5 gap-2">
              {effects.map((e) => (
                <button
                  key={e.id}
                  onClick={() => setEffect(e.id)}
                  disabled={isGenerating}
                  className={`flex flex-col items-center gap-1.5 px-2 py-3 rounded-xl text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${
                    effect === e.id ? 'bg-accent text-white shadow-lg' : 'bg-[#1e2025] text-gray-400 hover:bg-[#2d3139] border border-gray-700'
                  } ${isGenerating ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className={`${e.icon} text-base`}></i>
                  </div>
                  <span className="text-[10px] leading-tight">{t(e.labelKey)}</span>
                </button>
              ))}
            </div>
          </div>

          {/* 生成按钮 */}
          <button
            onClick={handleGenerate}
            disabled={isGenerating}
            className={`w-full px-6 py-4 rounded-xl font-black text-base transition-all flex items-center justify-center gap-3 whitespace-nowrap mb-4 ${
              !isGenerating
                ? 'bg-accent text-white hover:bg-accent/90 hover:shadow-xl hover:shadow-accent/30 cursor-pointer'
                : 'bg-gray-700 text-gray-500 cursor-not-allowed'
            }`}
          >
            {isGenerating ? (
              <>
                <i className="ri-loader-4-line text-xl animate-spin"></i>
                {t('i2v.generating')} ({progress}%)
              </>
            ) : (
              <>
                <i className="ri-video-line text-xl"></i>
                {t('i2v.generateBtn')}
              </>
            )}
          </button>

          {/* 进度条 */}
          {isGenerating && (
            <div className="mb-4">
              <div className="w-full h-2 bg-[#1e2025] rounded-full overflow-hidden">
                <div
                  className="h-full bg-accent rounded-full transition-all duration-300 ease-out"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <p className="text-xs text-gray-500 font-medium mt-2 text-center">{t('i2v.progressHint')}</p>
            </div>
          )}
        </>
      )}

      {/* 错误提示 */}
      {error && (
        <div className="mt-4 flex items-center gap-2 px-4 py-3 bg-red-500/10 border border-red-500/30 rounded-xl">
          <i className="ri-error-warning-line text-red-400"></i>
          <p className="text-red-400 text-sm font-semibold">{error}</p>
        </div>
      )}

      {/* 视频结果 */}
      {videoUrl && (
        <div className="mt-6 space-y-4">
          <div className="flex items-center gap-2 px-4 py-3 bg-green-500/10 border border-green-500/30 rounded-xl">
            <i className="ri-check-circle-fill text-green-400"></i>
            <p className="text-green-300 text-sm font-bold">{t('i2v.success')}</p>
          </div>
          <div className="rounded-xl overflow-hidden border border-gray-700 bg-black">
            <video ref={videoRef} src={videoUrl} controls autoPlay loop className="w-full max-h-[400px] object-contain" />
          </div>
          <div className="flex gap-3">
            <button
              onClick={handleDownload}
              className="flex-1 px-6 py-4 bg-primary text-white rounded-xl font-bold text-base hover:bg-primary/90 transition-all flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-download-line text-xl"></i>{t('i2v.download')}
            </button>
            <button
              onClick={() => { if (videoUrl) URL.revokeObjectURL(videoUrl); setVideoUrl(null); }}
              className="px-6 py-4 bg-[#1e2025] text-gray-300 rounded-xl font-bold text-base hover:bg-[#2d3139] transition-all flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap border border-gray-700"
            >
              <i className="ri-refresh-line text-xl"></i>{t('i2v.regenerate')}
            </button>
          </div>
        </div>
      )}

      <canvas ref={canvasRef} className="hidden" />
    </div>
  );
}